import React from 'react';
import {
  Button,
  Spacing,
  Typography,
  SelectedQuestionsSummary,
  ExamQuestionList,
  ExamGeneratorQuestionInterface,
  ExpandableCard,
  Badge,
  StyledDiv,
  Checkbox,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { transformQuestionTag } from '@taleemabad/dsm/utils';

export interface PercentageLevel {
  name: string;
  percentage: number;
  color: string;
}
interface SelectedQuestions {
  id: number;
}
export interface ExamQuestionsTemplateProps {
  questions: ExamGeneratorQuestionInterface[];
  selectedQuestions: SelectedQuestions[];
  selectedQuestionsMarks: number;
  questionTypeHeading: string;
  toggleQuestionSelection: (question: any) => void;
  onAddToExam: () => void;
  totalMarks: number;
  taxonomyPercentages: PercentageLevel[];
  questionCategoryPercentages: PercentageLevel[];
  selectAllQuestions?: boolean;
  toggleSelectAll?: (selectAll: boolean) => void;
  tempSelectedQuestions?: SelectedQuestions[];
}

export const ExamQuestionsTemplate: React.FC<ExamQuestionsTemplateProps> = ({
  questions,
  selectedQuestions,
  selectedQuestionsMarks,
  questionTypeHeading,
  toggleQuestionSelection,
  onAddToExam,
  totalMarks,
  taxonomyPercentages,
  questionCategoryPercentages,
  selectAllQuestions = false,
  toggleSelectAll,
  tempSelectedQuestions,
}) => {
  return (
    <>
      <SelectedOptionsWrapper>
        <SelectedQuestionsSummary
          questionTypeHeading={questionTypeHeading}
          totalMarks={totalMarks}
          selectedQuestionsMarks={selectedQuestionsMarks}
        />
      </SelectedOptionsWrapper>
      <ExpandableCard
        title="Selected Questions Percentage"
        defaultExpanded={true}
      >
        <Spacing marginTop="sm">
          <StyledDiv gap={4} justifyContent="space-between" flexWrap="wrap">
            {questionCategoryPercentages.map((item: any) => (
              <StyledBadgeItem key={item.name}>
                <Badge
                  size="large"
                  label={transformQuestionTag(item.name)}
                  color={item.color}
                />
                <StyledBadgePercentage variant="textTitleSmallonSurface">
                  {item.percentage}%
                </StyledBadgePercentage>
              </StyledBadgeItem>
            ))}
          </StyledDiv>
        </Spacing>
      </ExpandableCard>

      <ExpandableCard title="Bloom's Taxonomy Levels" defaultExpanded={true}>
        <Spacing marginTop="sm">
          <StyledDiv gap={4} justifyContent="space-between" flexWrap="wrap">
            {taxonomyPercentages.map((item: any) => (
              <StyledBadgeItem key={item.name}>
                <Badge size="small" label={item.name} color={item.color} />
                <StyledBadgePercentage variant="textTitleSmallonSurface">
                  {item.percentage}%
                </StyledBadgePercentage>
              </StyledBadgeItem>
            ))}
          </StyledDiv>
        </Spacing>
      </ExpandableCard>
      <Spacing marginTop="xl">
        <Typography
          variant="textLabelLargeOnSurface"
          data-cy="exam-generator-select-questions-title"
        >
          Select questions for your exam
        </Typography>
      </Spacing>
      {process.env.NX_PUBLIC_ENV === 'staging' && toggleSelectAll && (
        <Spacing marginTop="md" marginBottom="md">
          <Checkbox
            label="Select All Questions"
            checked={selectAllQuestions}
            onChange={toggleSelectAll}
          />
        </Spacing>
      )}
      <ExamQuestionList
        questions={questions}
        selectedQuestions={tempSelectedQuestions || selectedQuestions}
        toggleQuestionSelection={toggleQuestionSelection}
      />
      <AddToExamWrapper>
        <Button
          data-cy="exam-generator-add-to-exam-button"
          icon="add"
          children="Add To Exam"
          variant="filled"
          onClick={onAddToExam}
        />
      </AddToExamWrapper>
    </>
  );
};

const SelectedOptionsWrapper = styled.div`
  position: sticky;
  padding: var(--rk-spacing-lg) 0;
  top: 0px;
  z-index: 1;
  background: var(--md-sys-color-on-primary);
`;
const AddToExamWrapper = styled.div`
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;
  margin-right: var(--rk-spacing-xl);

  ${mq(BreakpointSizes.xs)} {
    md-filled-button {
      width: 100%;
    }
  }
`;

const StyledBadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  width: calc(50% - var(--rk-spacing-lg));
`;

const StyledBadgePercentage = styled(Typography)`
  text-align: right;
  flex-grow: 1;
`;
